import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, LucideIcon, Lock, Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export interface ToolBenefit {
  icon: LucideIcon;
  text: string;
  description?: string;
}

interface ToolActivationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Branding
  toolName: string;
  toolColor?: string;
  // Copy
  headline: string;
  subheadline: string;
  benefitsTitle: string;
  benefits: ToolBenefit[];
  formTitle: string;
  formDescription: string;
  planBadge: string;
  planText: string;
  // Success copy
  successTitle: string;
  successText: string;
  successCta: string;
  onAccessTool: () => void;
  loginUrl?: string;
}

type ModalState = 'form' | 'loading' | 'success' | 'error';

export const ToolActivationModal: React.FC<ToolActivationModalProps> = ({
  open,
  onOpenChange,
  toolName,
  toolColor = '#2563EB',
  headline,
  subheadline,
  benefitsTitle,
  benefits,
  formTitle,
  formDescription,
  onAccessTool,
}) => {
  useLanguage();
  const { user } = useAuth();
  const fullName = user?.nome || '';
  const email = user?.email || '';
  const [state, setState] = useState<ModalState>('form');
  useEffect(() => {
    if (open) {
      setState('form');
    }
  }, [open]);
  const handleSubmit = async () => {
    if (toolName == 'FunnelX') {
      //ex: https://funnelx.com.br/sign-up?name=Matheus%20Silva&email=matheus%40exemplo.com
      const quizUrl = "https://funnelx.com.br/sign-up?name=" + user.nome + "&email=" + user.email
      window.open(quizUrl.toString(), '_blank');
      onAccessTool();
      onOpenChange(false);
      setState('form');
    }

    if (toolName == 'Replic') {
      //https://app.replic.com.br/register?name=Matheus%20Silva&email=matheus%40exemplo.com
      const quizUrl = "https://app.replic.com.br/register?name=" + user.nome + "&email=" + user.email
      window.open(quizUrl.toString(), '_blank');
      onAccessTool();
      onOpenChange(false);
      setState('form');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="p-0 gap-0 border-[#E4E7EC] w-[min(94vw,600px)] max-w-none rounded-[18px] bg-white flex flex-col max-h-[90vh] overflow-hidden"
        style={{ boxShadow: '0 24px 70px rgba(15,23,42,0.18)' }}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-5 flex items-start justify-between gap-4 relative flex-shrink-0">
          <div className="flex-1 min-w-0">
            <span
              className="inline-flex items-center text-[10.5px] font-semibold uppercase tracking-[0.1em] px-2.5 py-1 rounded-full mb-3"
              style={{ background: toolColor, color: '#fff' }}
            >
              {translateInterfaceText("Teste grátis")}</span>
            <h2 className="text-[22px] font-semibold text-[#0B1220] leading-[1.2] tracking-[-0.01em]">
              {headline}
            </h2>
            <p className="text-[14px] text-[#4B5563] mt-2 leading-[1.55] max-w-[460px]">
              {subheadline}
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] hover:text-[#0F172A] hover:border-[#D1D5DB] transition-all flex-shrink-0"
            aria-label={translateInterfaceText("Fechar")}
          >
            <X className="w-[15px] h-[15px]" strokeWidth={2} />
          </button>
        </div>

        {/* Body */}
        <div className="px-7 pb-3 flex-1 overflow-y-auto min-h-0">
          {/* Benefits card */}
          <div className="rounded-[14px] border border-[#DCE6FF] bg-[#EAF1FF] p-4 mb-6">
            <div className="text-[12px] font-semibold text-[#0B1220] mb-3 tracking-[0.01em]">
              {benefitsTitle}
            </div>
            <div className="flex flex-col gap-2.5">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-[12px] bg-white border border-[#E4ECFF] p-3"
                  >
                    <div
                      className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                      style={{ background: `${toolColor}14` }}
                    >
                      <Icon className="w-[18px] h-[18px]" style={{ color: toolColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold text-[#0F172A] leading-tight">
                        {b.text}
                      </div>
                      {b.description && (
                        <div className="text-[12px] text-[#6B7280] mt-1 leading-[1.5]">
                          {b.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="mb-5">
            <h3 className="text-[15.5px] font-semibold text-[#0B1220] mb-1 tracking-[-0.005em]">
              {formTitle}
            </h3>
            <p className="text-[13px] text-[#6B7280] mb-4 leading-[1.55]">
              {formDescription}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <Label className="text-[12.5px] font-medium text-[#374151] mb-1.5 block">
                  {translateInterfaceText("Nome completo")}</Label>
                <div className="relative">
                  <Input
                    value={fullName || 'Usuário Host VSL'}
                    readOnly
                    className="bg-[#F9FAFB] border-[#E5E7EB] text-[#1F2937] cursor-default pr-9 h-10"
                  />
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
                </div>
              </div>
              <div>
                <Label className="text-[12.5px] font-medium text-[#374151] mb-1.5 block">{translateInterfaceText("E-mail")}</Label>
                <div className="relative">
                  <Input
                    value={email || 'voce@hostvsl.com'}
                    readOnly
                    className="bg-[#F9FAFB] border-[#E5E7EB] text-[#1F2937] cursor-default pr-9 h-10"
                  />
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-[#EEF0F3] bg-[#FCFCFD] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2.5 flex-shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#6B7280] hover:bg-transparent hover:text-[#6B7280] h-10" disabled={state === 'loading'}>
            {translateInterfaceText("Agora não")}</Button>
          <Button
            onClick={handleSubmit}
            disabled={state === 'loading'}
            style={{ background: toolColor, color: '#fff' }}
            className="hover:opacity-90 h-10 font-semibold px-5 shadow-sm min-w-[170px]"
          >
            {state === 'loading' ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {translateInterfaceText("Acessando...")}</span>
            ) : (
              translateInterfaceText("Ativar acesso grátis")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
